import { useState, useCallback, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Plus, Link2, Unlink, Search, RefreshCw } from 'lucide-react'
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/components/ui/select'
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '@/components/ui/dialog'
import { distributorApi, DistributorItem } from '@/api/distributors'
import { PermissionGuard } from '@/components/auth/PermissionGuard'
import { DEFAULT_PAGE_SIZE, ListPagination } from '@/components/common/ListPagination'
import { BindDistributorDialog } from './BindDistributorDialog'

export function DistributorsPage() {
	const [searchParams, setSearchParams] = useSearchParams()
	const [pageSize, setPageSize] = useState(() => parseInt(searchParams.get('page_size') || String(DEFAULT_PAGE_SIZE), 10))

	// 状态
	const [keyword, setKeyword] = useState(searchParams.get('keyword') || '')
	const [statusFilter, setStatusFilter] = useState<string>(searchParams.get('status') || '1')
	const [data, setData] = useState<{ list: DistributorItem[]; total: number } | null>(null)
	const [isLoading, setIsLoading] = useState(true)
	const [isFetching, setIsFetching] = useState(false)
	const [error, setError] = useState<string | null>(null)

	// 弹窗状态
	const [bindDialogOpen, setBindDialogOpen] = useState(false)
	const [unbindDialogOpen, setUnbindDialogOpen] = useState(false)
	const [selectedDistributor, setSelectedDistributor] = useState<DistributorItem | null>(null)
	const [isUnbinding, setIsUnbinding] = useState(false)

	// 从 URL 获取页码
	const page = parseInt(searchParams.get('page') || '1', 10)

	// 加载数据
	const loadData = useCallback(async (showLoading = false) => {
		if (showLoading) setIsLoading(true)
		setIsFetching(true)
		setError(null)
		try {
			const params: Record<string, unknown> = {
				page,
				page_size: pageSize,
			}
			if (keyword) params.keyword = keyword
			if (statusFilter && statusFilter !== 'all') params.status = parseInt(statusFilter, 10)

			const result = await distributorApi.list(params as any)
			setData(result)
		} catch (err) {
			setError((err as Error).message || '加载失败')
		} finally {
			setIsFetching(false)
			setIsLoading(false)
		}
	}, [page, pageSize, keyword, statusFilter])

	// 初始加载和参数变化时加载
	useEffect(() => {
		loadData(true)
	}, [loadData])

	// 同步搜索参数到 URL
	const syncToUrl = (newPage = 1, newKeyword = keyword, newStatus = statusFilter, newPageSize = pageSize) => {
		const urlParams = new URLSearchParams()
		if (newKeyword) urlParams.set('keyword', newKeyword)
		if (newStatus && newStatus !== 'all') urlParams.set('status', newStatus)
		if (newPage > 1) urlParams.set('page', String(newPage))
		if (newPageSize !== DEFAULT_PAGE_SIZE) urlParams.set('page_size', String(newPageSize))
		setSearchParams(urlParams, { replace: true })
	}

	// 搜索
	const handleSearch = () => {
		syncToUrl(1)
	}

	// 重置
	const handleReset = () => {
		setKeyword('')
		setStatusFilter('1')
		syncToUrl(1, '', '1')
	}

	// 分页
	const handlePageChange = (newPage: number) => {
		syncToUrl(newPage)
	}

	const handlePageSizeChange = (newPageSize: number) => {
		setPageSize(newPageSize)
		syncToUrl(1, keyword, statusFilter, newPageSize)
	}

	// 打开绑定弹窗
	const handleOpenBindDialog = (distributor?: DistributorItem) => {
		setSelectedDistributor(distributor || null)
		setBindDialogOpen(true)
	}

	// 打开解绑弹窗
	const handleOpenUnbindDialog = (distributor: DistributorItem) => {
		setSelectedDistributor(distributor)
		setUnbindDialogOpen(true)
	}

	// 确认解绑
	const handleConfirmUnbind = async () => {
		if (!selectedDistributor) return
		setIsUnbinding(true)
		try {
			await distributorApi.unbind(selectedDistributor.id)
			setUnbindDialogOpen(false)
			setSelectedDistributor(null)
			loadData()
		} catch (err) {
			setError((err as Error).message || '解绑失败')
		} finally {
			setIsUnbinding(false)
		}
	}

	// 绑定成功回调
	const handleBindSuccess = () => {
		setBindDialogOpen(false)
		setSelectedDistributor(null)
		loadData()
	}

	return (
		<div className="space-y-6">
			{/* 页面标题 */}
			<div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
				<div>
					<h1 className="text-2xl font-bold tracking-tight">分销商管理</h1>
					<p className="text-muted-foreground">管理后台用户的供应商身份绑定</p>
				</div>
				<PermissionGuard permission="distributor:bind">
					<Button onClick={() => handleOpenBindDialog()}>
						<Plus className="mr-1.5 h-4 w-4" />
						绑定供应商
					</Button>
				</PermissionGuard>
			</div>

			{/* 搜索筛选 */}
			<Card>
				<CardContent className="pt-6">
					<div className="flex flex-col gap-4 sm:flex-row sm:items-end">
						<div className="flex-1 grid gap-4 sm:grid-cols-2">
							<div className="space-y-2">
								<label htmlFor="distributor-search-keyword" className="text-sm font-medium text-muted-foreground">搜索关键词</label>
								<Input
									id="distributor-search-keyword"
									placeholder="用户名 / 昵称 / 手机号"
									value={keyword}
									onChange={(e) => setKeyword(e.target.value)}
									onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
								/>
							</div>
							<div className="space-y-2">
								<label htmlFor="distributor-search-status" className="text-sm font-medium text-muted-foreground">绑定状态</label>
								<Select value={statusFilter} onValueChange={setStatusFilter}>
									<SelectTrigger id="distributor-search-status">
										<SelectValue placeholder="全部状态" />
									</SelectTrigger>
									<SelectContent>
										<SelectItem value="all">全部状态</SelectItem>
										<SelectItem value="1">已绑定</SelectItem>
										<SelectItem value="0">未绑定</SelectItem>
									</SelectContent>
								</Select>
							</div>
						</div>
						<div className="flex gap-2">
							<Button variant="outline" onClick={handleReset}>
								<RefreshCw className="mr-1.5 h-4 w-4" />
								重置
							</Button>
							<Button onClick={handleSearch}>
								<Search className="mr-1.5 h-4 w-4" />
								搜索
							</Button>
						</div>
					</div>
				</CardContent>
			</Card>

			{/* 分销商表格 */}
			<Card>
				<CardContent className="p-0">
					{isLoading ? (
						<div className="p-4 space-y-3">
							{Array.from({ length: 5 }).map((_, i) => (
								<div key={i} className="flex items-center gap-4">
									<Skeleton className="h-10 w-10 rounded-full" />
									<div className="flex-1 space-y-2">
										<Skeleton className="h-4 w-32" />
										<Skeleton className="h-3 w-48" />
									</div>
									<Skeleton className="h-6 w-16" />
								</div>
							))}
						</div>
					) : error ? (
						<div className="p-8 text-center">
							<p className="text-destructive">{error}</p>
							<Button variant="outline" className="mt-4" onClick={() => loadData()}>
								重试
							</Button>
						</div>
					) : (
						<Table>
							<TableHeader>
								<TableRow>
									<TableHead>后台用户</TableHead>
									<TableHead>手机号</TableHead>
									<TableHead>CRMEB供应商</TableHead>
									<TableHead>供应商名称</TableHead>
									<TableHead>供应商手机</TableHead>
									<TableHead className="text-right">操作</TableHead>
								</TableRow>
							</TableHeader>
							<TableBody>
								{!data?.list?.length ? (
									<TableRow>
										<TableCell colSpan={6} className="h-24 text-center">
											暂无数据
										</TableCell>
									</TableRow>
								) : (
									data.list.map((item) => {
										const isBound = item.crmeb_uid && item.crmeb_uid > 0
										return (
											<TableRow key={item.id}>
												<TableCell>
													<div className="flex items-center gap-3">
														<div className="flex h-9 w-9 items-center justify-center rounded-full bg-muted">
															<span className="text-sm font-medium">
																{(item.nickname || item.username || '?').charAt(0).toUpperCase()}
															</span>
														</div>
														<div>
															<p className="font-medium">{item.nickname || item.username}</p>
															<p className="text-xs text-muted-foreground">@{item.username}</p>
														</div>
													</div>
												</TableCell>
												<TableCell className="text-muted-foreground">
													{item.phone || '-'}
												</TableCell>
												<TableCell>
													{isBound ? (
														<div className="flex items-center gap-2">
															<Badge variant="default" className="gap-1">
																<Link2 className="h-3 w-3" />
																#{item.crmeb_uid}
															</Badge>
															<span className="text-sm">{item.crmeb_account || '未知账号'}</span>
														</div>
													) : (
														<span className="text-muted-foreground">-</span>
													)}
												</TableCell>
												<TableCell>
													{isBound ? (
														<span>{item.supplier_name || item.crmeb_real_name || '-'}</span>
													) : (
														<span className="text-muted-foreground">-</span>
													)}
												</TableCell>
												<TableCell className="text-muted-foreground">
													{isBound ? (item.crmeb_phone || '-') : '-'}
												</TableCell>
												<TableCell className="text-right">
													<div className="flex items-center justify-end gap-1">
														<PermissionGuard permission="distributor:bind">
															{isBound ? (
																<Button
																	variant="ghost"
																	size="sm"
																	className="h-8 text-destructive hover:text-destructive"
																	onClick={() => handleOpenUnbindDialog(item)}
																>
																	<Unlink className="mr-1 h-4 w-4" />
																	解绑
																</Button>
															) : (
																<Button
																	variant="ghost"
																	size="sm"
																	className="h-8"
																	onClick={() => handleOpenBindDialog(item)}
																>
																	<Link2 className="mr-1 h-4 w-4" />
																	绑定
																</Button>
															)}
														</PermissionGuard>
													</div>
												</TableCell>
											</TableRow>
										)
									})
								)}
							</TableBody>
						</Table>
					)}
				</CardContent>
			</Card>

			{/* 分页 */}
			<ListPagination
				page={page}
				pageSize={pageSize}
				total={data?.total || 0}
				loading={isFetching}
				showPageNumbers={false}
				onPageChange={handlePageChange}
				onPageSizeChange={handlePageSizeChange}
			/>

			{/* 绑定弹窗 */}
			<BindDistributorDialog
				open={bindDialogOpen}
				onOpenChange={setBindDialogOpen}
				onSuccess={handleBindSuccess}
			/>

			{/* 解绑确认弹窗 */}
			<Dialog open={unbindDialogOpen} onOpenChange={setUnbindDialogOpen}>
				<DialogContent className="sm:max-w-[400px]">
					<DialogHeader>
						<DialogTitle>确认解绑</DialogTitle>
						<DialogDescription>
							确定要解绑用户 <span className="font-medium text-foreground">{selectedDistributor?.nickname || selectedDistributor?.username}</span> 与 CRMEB 供应商 <span className="font-medium text-foreground">#{selectedDistributor?.crmeb_uid}</span> 的绑定关系吗？
						</DialogDescription>
					</DialogHeader>
					<DialogFooter>
						<Button variant="outline" onClick={() => setUnbindDialogOpen(false)} disabled={isUnbinding}>
							取消
						</Button>
						<Button variant="destructive" onClick={handleConfirmUnbind} disabled={isUnbinding}>
							{isUnbinding ? '解绑中...' : '确认解绑'}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</div>
	)
}

export default DistributorsPage
